/**
 * ForgotPasswordPage.tsx
 *
 * Staff-side "forgot password" entry point. Submits a username, the backend
 * looks up the account and emails a reset link.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { authApi } from '@/services/api';
import { getLegacyOrigin } from '@/lib/appStage';

const DEMO_LOGIN_NAME = 'nexthealth';
const PROSPERA_LOGO_URL = `${getLegacyOrigin()}/uimages/img8/brandlogo.png`;

export function ForgotPasswordPage() {
  const [loginName] = useState(DEMO_LOGIN_NAME);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.forgotPassword(loginName.trim(), username.trim());
      setSubmitted(true);
    } catch (err: unknown) {
      // Don't reveal whether the username exists — generic confirmation either way.
      // We still surface infrastructure errors so the user knows to retry.
      const code = (err as { response?: { status?: number } })?.response?.status;
      if (code === 404 || code === 200) {
        setSubmitted(true);
      } else {
        setError(
          (err as { userMessage?: string })?.userMessage
            ?? 'Something went wrong. Please try again in a moment.',
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <img
            src={PROSPERA_LOGO_URL}
            alt="Prospera"
            className="h-auto w-full max-w-[280px] object-contain"
          />
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)] space-y-5">
          <div>
            <h1 className="text-lg font-semibold text-white">Reset your password</h1>
            <p className="mt-1 text-sm text-slate-400">
              Enter your username and we'll email a reset link to the address on file.
            </p>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  If an account matches that username, a reset link is on its way.
                  Check your email — the link expires in 30 minutes.
                </div>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="loginName" value={loginName} />

                <div>
                  <label htmlFor="username" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    autoFocus
                    className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40"
                    placeholder="you@example.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !username}
                  className="w-full rounded-md bg-[#6558f5] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#766af8] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>
              </form>

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
